import { LocationEntity, MeetingEntity } from '@/types/entities'
import { MeetingFormat } from '@/types/misc'
import { BookableMeetingFormat } from '@/types/requests/meetings'
import { AvailabilityItem } from '@/types/responses/availability'

export interface SchedulerParticipant {
  name: string
  email: string
  nameReadOnly?: boolean
  emailReadOnly?: boolean
}

export interface MeetingDetails {
  start: Date
  end: Date
  /**
   * Minimum of one participant.
   */
  participants: [SchedulerParticipant, ...SchedulerParticipant[]]
  timezone?: string
  format: BookableMeetingFormat
  /**
   * The office to meet at, for an in-person office meeting, or the office
   * handling the visit (optional), for a meeting at the client's home.
   */
  locationId?: number
  /**
   * The client's address, for a meeting at the client's home only.
   */
  clientAddressLine1?: string
  clientTownOrCity?: string
  clientPostcode?: string
}

/**
 * The office a meeting was booked at, as the API returns it with the meeting.
 */
export type BookedMeetingLocation = Pick<
  LocationEntity,
  | 'name'
  | 'addressLine1'
  | 'addressLine2'
  | 'townOrCity'
  | 'postcode'
  | 'country'
  | 'clientNotes'
>

export type BookedMeetingDetails = Pick<
  MeetingDetails,
  'start' | 'end' | 'timezone' | 'participants'
> &
  Pick<
    MeetingEntity,
    'clientAddressLine1' | 'clientTownOrCity' | 'clientPostcode'
  > & {
    /**
     * The office the meeting is at, or the office handling a visit to the
     * client's home. Null when there's neither.
     */
    location: BookedMeetingLocation | null
    format: MeetingFormat | null
    invitesSent: boolean
    bookedWithFullName: string
  }

/**
 * Where the meeting being rescheduled is now, so the scheduler starts from the
 * same format, office and address. The address is null when it's hidden from
 * the person rescheduling.
 */
export type MeetingBeingRescheduled = Pick<
  MeetingEntity,
  'format' | 'clientAddressLine1' | 'clientTownOrCity' | 'clientPostcode'
> & {
  location: Pick<LocationEntity, 'id'> | null
}

export enum SchedulerOwningEntityType {
  Company = 'companies',
  Referral = 'referrals',
  ReferralLink = 'referral-links',
}

/**
 * Overrides where the scheduler loads availability from (e.g. when
 * rescheduling an existing meeting). `key` keeps its cached results separate
 * from the default availability of the same owning entity.
 */
export interface SchedulerAvailabilitySource {
  key: string
  fetchAvailability: (
    start: Date,
    end: Date,
    availabilityFilter: AvailabilityFilter,
  ) => Promise<AvailabilityItem>
}

/**
 * The first question on the scheduler: how to meet. "In person" covers both
 * in-person formats; "Where?" then decides between them when both are offered.
 */
export type MeetingFormatChoice = 'video' | 'phone' | 'in_person'

export type InPersonMeetingFormat = Extract<
  MeetingFormat,
  'in_person_office' | 'in_person_client_home'
>

/**
 * The client's address for a meeting at their home, as typed into the
 * scheduler. Empty strings until filled in.
 */
export interface ClientHomeAddress {
  addressLine1: string
  townOrCity: string
  postcode: string
}

export const EMPTY_CLIENT_HOME_ADDRESS: ClientHomeAddress = {
  addressLine1: '',
  townOrCity: '',
  postcode: '',
}

/**
 * Everything the person has chosen about the meeting's format and place, as
 * they chose it (before any fallback for options that aren't offered). Kept
 * by the page that hosts the scheduler so the answers survive leaving and
 * coming back to the step.
 */
export interface SchedulerSelection {
  formatChoice: MeetingFormatChoice | null
  inPersonFormat: InPersonMeetingFormat | null
  /**
   * The office to meet at (in-person office meeting) or the office handling
   * the visit (meeting at the client's home).
   */
  locationId: number | null
  clientHomeAddress: ClientHomeAddress
}

export const EMPTY_SCHEDULER_SELECTION: SchedulerSelection = {
  formatChoice: null,
  inPersonFormat: null,
  locationId: null,
  clientHomeAddress: EMPTY_CLIENT_HOME_ADDRESS,
}

/**
 * Which advisers' availability to show, and under which booking rules.
 */
export interface AvailabilityFilter {
  /** Only advisers based at this office, or every adviser when null. */
  locationId: number | null
  /**
   * The in-person format being booked, so the API applies the meeting type's
   * in-person booking rules and, with an office, the right office rules. Null
   * for a video or phone meeting.
   */
  inPersonFormat: InPersonMeetingFormat | null
}

export const EVERY_ADVISER_AVAILABILITY_FILTER: AvailabilityFilter = {
  locationId: null,
  inPersonFormat: null,
}
